import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table, { TableColumns } from "../../components/table";
import Alert from "../../components/alert";
import useScreenName from "../../hooks/useScreenName";
import dispatch, { ForbiddenError } from "../../middleware";
import { getEmployers } from "../../actions/employer.action";
import { ReduxState } from "../../reducers";
import { Employer } from "../../reducers/employer.reducer";
import { splitKeyWithSpace } from "../../utils";
import Pagination from "../../components/pagination";
import { SearchBox } from "../../components/input-functions";
import { EmployerActions } from "../../actions/employer.action";

const Employers = () => {
  useScreenName("Employers");
  const storeDispatch = useDispatch();
  const employers = useSelector((state: ReduxState) => state.employer.employers);
  const loadingQueue = useSelector((state: ReduxState) => state.employer.loadingQueue);
  const isLoading = loadingQueue.includes(EmployerActions.REQUEST_GET_EMPLOYERS);

  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  useEffect(() => {
    dispatch(storeDispatch, getEmployers()).catch((error) => {
      if (error instanceof ForbiddenError) {
        setNotAuthorized(true);
      }
    });
  }, [storeDispatch]);

  const EMPLOYERS_TABLE_COLUMNS: TableColumns[] = [
    { field: "_id", label: "ID", style: { textAlign: "center" } },
    { field: "name", label: "Name" },
    { field: "clientRefId", label: "Client Ref ID", style: { wordBreak: "break-all" } },
    { field: "segment", label: "Segment", style: { whiteSpace: "nowrap", textAlign: "center" } },
    { field: "createdAt", label: "Onboarded on", style: { textAlign: "center" } },
  ];

  const transformedData = employers.map((employer: Employer) => ({
    ...employer,
    _id: <Link to={`/employers/${employer._id}`}>{employer._id}</Link>,
    clientRefId: employer.clientRefId || "-",
    segment: splitKeyWithSpace(employer.segment),
    createdAt: new Date(employer.createdAt).toLocaleDateString(),
  }));

  const handleSearch = (searchText: string) => {
    setSearchText(searchText.toLowerCase().trim());
  };

  const filteredData = transformedData.filter((employer: Employer) => {
    if (!searchText) return true;
    return (
      (employer.name || "").toLowerCase().includes(searchText) ||
      (employer.clientRefId || "").toLowerCase().includes(searchText) ||
      (employer.segment || "").toLowerCase().includes(searchText)
    );
  });

  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + entriesPerPage);

  return (
    <>
      <div className="dashboard-container">
        <h2 className="mb-4">Employers</h2>
        {notAuthorized ? (
          <Alert type="danger" message="Not authorized to view employers." />
        ) : (
          <>
            <div className="mb-3">
              <SearchBox
                value={searchText}
                placeholder="Search by Name, Client Ref ID or Segment"
                onSubmit={handleSearch}
              />
            </div>
            <div style={{ marginTop: '20px' }}>
              <Table loading={isLoading} columns={EMPLOYERS_TABLE_COLUMNS} data={paginatedData} />
            </div>
            <Pagination
              currentPage={currentPage}
              totalEntries={filteredData.length}
              numberOfEntriesPerPage={entriesPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onPerPageCountChange={(count) => {
                setEntriesPerPage(count);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Employers;
